import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Calendar, 
  MapPin, 
  Users, 
  ArrowLeft, 
  ExternalLink, 
  Printer, 
  Download, 
  Plane, 
  Building, 
  Clock,
  UtensilsCrossed,
  Star,
  Coins,
  Gem,
  RefreshCw,
  Sparkles,
  Compass,
  Landmark,
  Moon,
  HeartHandshake,
  Wine
} from 'lucide-react';
import { api, formatApiError } from '../lib/api';

const COMMON_IATA = {
  'venezia': 'VCE', 'venice': 'VCE', 'marghera': 'VCE', 'mestre': 'VCE', 'treviso': 'TSF',
  'bari': 'BRI', 'roma': 'ROM', 'rome': 'ROM', 'fiumicino': 'FCO', 'ciampino': 'CIA',
  'milano': 'MIL', 'milan': 'MIL', 'malpensa': 'MXP', 'linate': 'LIN', 'bergamo': 'BGY',
  'napoli': 'NAP', 'naples': 'NAP', 'firenze': 'FLR', 'florence': 'FLR', 'pisa': 'PSA',
  'bologna': 'BLQ', 'palermo': 'PMO', 'catania': 'CTA', 'torino': 'TRN', 'turin': 'TRN',
  'verona': 'VRN', 'genova': 'GOA', 'genoa': 'GOA', 'cagliari': 'CAG', 'olbia': 'OLB',
  'alghero': 'AHO', 'brindisi': 'BDS', 'lamezia': 'SUF', 'trieste': 'TRS', 'reggio calabria': 'REG',
  'tirana': 'TIA', 'vienna': 'VIE', 'parigi': 'PAR', 'londra': 'LON', 'madrid': 'MAD',
  'barcellona': 'BCN', 'lisbona': 'LIS', 'berlino': 'BER', 'amsterdam': 'AMS', 'praga': 'PRG'
};

function formatDateToDDMM(dateStr) {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  return parts.length === 3 ? `${parts[2]}${parts[1]}` : '';
}

export default function TripDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [destIata, setDestIata] = useState('');
  
  // Traccia lo stile selezionato e lo stato di caricamento per ogni singolo giorno
  const [dayStyles, setDayStyles] = useState({});
  const [regeneratingDay, setRegeneratingDay] = useState(null);

  useEffect(() => {
    const fetchTrip = async () => {
      try {
        const res = await api.get(`/trips/${id}`);
        setTrip(res.data);

        // Inizializza lo stile di default per ciascun giorno
        if (res.data?.days) {
          const initialStyles = {};
          res.data.days.forEach(d => {
            initialStyles[d.day] = res.data.trip_style || 'mix';
          });
          setDayStyles(initialStyles);
        }

        if (res.data?.destination) {
          const clean = res.data.destination.trim().toLowerCase();
          if (COMMON_IATA[clean]) {
            setDestIata(COMMON_IATA[clean]);
          } else {
            fetch(`https://autocomplete.travelpayouts.com/places2?locale=it&types[]=city&types[]=airport&term=${encodeURIComponent(clean)}`)
              .then(r => r.json())
              .then(data => {
                if (data && data.length > 0) setDestIata(data[0].code || data[0].city_code || '');
              })
              .catch(() => {});
          }
        }
      } catch (err) {
        console.error('Errore caricamento dettaglio viaggio:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTrip();
  }, [id]);

  const isEn = trip?.language === 'en';

  const handleDayStyleChange = (dayNum, style) => {
    setDayStyles(prev => ({ ...prev, [dayNum]: style }));
  };

  const handleRegenerateDay = async (dayNum) => {
    const selectedStyle = dayStyles[dayNum] || 'mix';
    setRegeneratingDay(dayNum);
    try {
      const res = await api.post(`/trips/${id}/regenerate-day`, {
        day: dayNum,
        trip_style: selectedStyle
      });
      setTrip(res.data);
    } catch (err) {
      alert((isEn ? "Failed to rework day: " : "Errore nella rielaborazione del giorno: ") + (formatApiError ? formatApiError(err) : "Error"));
    } finally {
      setRegeneratingDay(null);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow || !trip) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="${isEn ? 'en' : 'it'}">
      <head>
        <meta charset="UTF-8">
        <title>${trip.title || (isEn ? 'Trip' : 'Viaggio')}</title>
        <style>
          body { font-family: Arial, sans-serif; background: #ffffff; color: #333333; padding: 30px; margin: 0; }
          h1 { color: #1a237e; font-size: 24px; margin-bottom: 5px; }
          .subtitle { color: #666; font-size: 13px; margin-bottom: 20px; }
          .overview { background: #f5f5f5; padding: 15px; border-radius: 8px; margin-bottom: 25px; line-height: 1.5; }
          .day-box { border: 1px solid #ddd; padding: 15px; border-radius: 8px; margin-bottom: 15px; page-break-inside: avoid; }
          .day-title { color: #b8860b; font-size: 16px; border-bottom: 1px solid #eee; padding-bottom: 6px; margin-top: 0; }
          .activity { background: #fafafa; padding: 8px 12px; border-radius: 6px; margin-top: 8px; border: 1px solid #eee; }
        </style>
      </head>
      <body>
        <h1>${trip.title || `${isEn ? 'Trip to' : 'Viaggio a'} ${trip.destination}`}</h1>
        <div class="subtitle">${isEn ? 'Dates:' : 'Periodo:'} ${trip.departure_date || ''} - ${trip.return_date || ''} | ${isEn ? 'Destination:' : 'Destinazione:'} ${trip.destination || ''}</div>
        ${trip.overview ? `<div class="overview">${trip.overview}</div>` : ''}
        <h2>${isEn ? 'Daily Itinerary' : 'Programma di Viaggio'}</h2>
        ${trip.days ? trip.days.map((d, i) => `
          <div class="day-box">
            <h3 class="day-title">${isEn ? 'Day' : 'Giorno'} ${d.day || i + 1} ${d.date ? `- ${d.date}` : ''}</h3>
            ${d.summary ? `<p><em>${d.summary}</em></p>` : ''}
            <div>
              ${d.activities ? d.activities.map(a => `
                <div class="activity">
                  <strong>[${a.time || (isEn ? 'Flexible' : 'Orario flessibile')}] ${a.title || ''}</strong>
                  ${a.description ? `<p style="margin: 4px 0 0 0; color: #555; font-size: 12px;">${a.description}</p>` : ''}
                </div>
              `).join('') : ''}
            </div>
          </div>
        `).join('') : ''}
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b1437] text-white flex items-center justify-center">
        <p className="text-gray-400">{isEn ? "Loading itinerary..." : "Caricamento itinerario..."}</p>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="min-h-screen bg-[#0b1437] text-white flex flex-col items-center justify-center p-4">
        <h2 className="text-2xl font-serif mb-4">{isEn ? "Trip not found" : "Viaggio non trovato"}</h2>
        <Link to="/dashboard" className="px-5 py-2.5 bg-[#d4af37] text-[#0b1437] font-semibold rounded-full text-sm">
          {isEn ? "Back to your trips" : "Torna ai tuoi viaggi"}
        </Link>
      </div>
    );
  }

  const marker = "752211";
  const depDateFormatted = formatDateToDDMM(trip.departure_date);
  const retDateFormatted = formatDateToDDMM(trip.return_date);
  const passengers = trip.people || 2;
  const originCode = COMMON_IATA[(trip.origin || 'venezia').toLowerCase()] || 'VCE';

  let flightUrl = '';
  if (destIata && depDateFormatted && retDateFormatted) {
    flightUrl = `https://www.aviasales.com/search/${originCode}${depDateFormatted}${destIata}${retDateFormatted}${passengers}?marker=${marker}`;
  } else {
    flightUrl = `https://www.aviasales.com/search?marker=${marker}&origin_iata=${originCode}&destination=${encodeURIComponent(trip.destination)}&depart_date=${trip.departure_date || ''}&return_date=${trip.return_date || ''}&adults=${passengers}`;
  }

  const hotelUrl = `https://search.hotellook.com/?destination=${encodeURIComponent(trip.destination)}&checkIn=${trip.departure_date || ''}&checkOut=${trip.return_date || ''}&adults=${passengers}&marker=${marker}`;

  const renderBudgetBadge = (level) => {
    switch (level?.toLowerCase()) {
      case 'economico':
        return (
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-950/60 border border-emerald-500/50 text-emerald-300 text-xs font-semibold">
            <Coins className="w-3.5 h-3.5 text-emerald-400" />
            <span>{isEn ? "Budget Friendly" : "Budget Economico"}</span>
          </div>
        );
      case 'lusso':
        return (
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-950/60 border border-purple-500/50 text-purple-300 text-xs font-semibold">
            <Gem className="w-3.5 h-3.5 text-purple-400" />
            <span>{isEn ? "Luxury Tier" : "Esperienza Lusso"}</span>
          </div>
        );
      default:
        return (
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-950/60 border border-amber-500/50 text-amber-300 text-xs font-semibold">
            <UtensilsCrossed className="w-3.5 h-3.5 text-amber-400" />
            <span>{isEn ? "Balanced Budget" : "Budget Medio"}</span>
          </div>
        );
    }
  };

  const styleOptions = [
    { id: 'mix', icon: Compass, label: isEn ? 'Mix' : 'Mix' },
    { id: 'cultura', icon: Landmark, label: isEn ? 'Culture' : 'Arte' },
    { id: 'movida', icon: Moon, label: isEn ? 'Nightlife' : 'Movida' },
    { id: 'benessere', icon: HeartHandshake, label: isEn ? 'Spa & Relax' : 'Relax' },
    { id: 'gusto', icon: Wine, label: isEn ? 'Food' : 'Gusto' },
  ];

  return (
    <div className="min-h-screen bg-[#0b1437] text-white px-4 py-8 md:px-12">
      <div className="max-w-4xl mx-auto">
        {/* Navigazione Superiore */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{isEn ? "Back" : "Indietro"}</span>
          </button>

          <div className="flex items-center gap-3">
            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#d4af37] text-[#0b1437] font-bold rounded-lg text-xs transition hover:bg-[#b8962d]"
            >
              <Printer className="w-3.5 h-3.5" />
              <span>{isEn ? "Print" : "Stampa"}</span>
            </button>
            <button
              onClick={handleDownloadPDF}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-xs text-white font-medium transition"
            >
              <Download className="w-3.5 h-3.5" />
              <span>{isEn ? "Download PDF" : "Scarica PDF"}</span>
            </button>
          </div>
        </div>

        {/* Intestazione Viaggio */}
        <div className="bg-[#12204f]/60 border border-gray-800 rounded-2xl p-6 md:p-8 mb-6 shadow-xl">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
            <span className="text-xs uppercase tracking-widest text-[#d4af37] font-semibold">
              {isEn ? "Genie Coordinated Itinerary" : "Itinerario Coordinato dal Genio"}
            </span>
            {renderBudgetBadge(trip.budget_level)}
          </div>

          <h1 className="text-3xl md:text-5xl font-serif text-[#ede7d8] mt-1 mb-3">
            {trip.title || `${isEn ? 'Trip to' : 'Viaggio a'} ${trip.destination}`}
          </h1>

          {trip.overview && (
            <p className="text-gray-300 text-sm md:text-base leading-relaxed mb-5">
              {trip.overview}
            </p>
          )}

          {/* Dettagli Logistici in pillole */}
          <div className="flex flex-wrap items-center gap-4 text-xs md:text-sm text-gray-300 border-t border-gray-800/80 pt-4">
            <div className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-[#d4af37]" />
              <span>{trip.departure_date} - {trip.return_date}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <MapPin className="w-4 h-4 text-[#d4af37]" />
              <span className="capitalize">{trip.destination}</span>
            </div>
            {trip.people && (
              <div className="flex items-center gap-1.5">
                <Users className="w-4 h-4 text-[#d4af37]" />
                <span>{trip.people} {isEn ? "travelers" : "persone"}</span>
              </div>
            )}
            {trip.flight_arrival_time && (
              <div className="flex items-center gap-1.5 text-amber-300">
                <Clock className="w-4 h-4" />
                <span>
                  {isEn 
                    ? `Arrival: ${trip.flight_arrival_time} | Return flight: ${trip.flight_departure_time}` 
                    : `Arrivo: ${trip.flight_arrival_time} | Decollo rientro: ${trip.flight_departure_time}`}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* BOX COMPATTO PER ACCEDERE AI TICKET VOLO E HOTEL */}
        <div className="bg-[#12204f]/40 border border-[#d4af37]/30 rounded-xl p-4 mb-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-xs text-gray-300 text-center sm:text-left">
            <span className="font-semibold text-white">
              {isEn ? "Already locked in your tickets?" : "Hai già bloccato i tuoi biglietti?"}
            </span>
            <p className="text-gray-400 text-[11px] mt-0.5">
              {isEn 
                ? "Compare best flights or book your stay via Aviasales & Hotellook:" 
                : "Rivedi i voli migliori o prenota l'alloggio su Aviasales & Hotellook:"}
            </p>
          </div>
          <div className="flex gap-2">
            <a
              href={flightUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#d4af37] text-[#0b1437] font-bold rounded-lg text-xs hover:bg-[#b8962d] transition"
            >
              <Plane className="w-3.5 h-3.5" />
              <span>{isEn ? "Flights" : "Voli"}</span>
              <ExternalLink className="w-3 h-3 opacity-75" />
            </a>
            <a
              href={hotelUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1b2559] border border-[#d4af37]/50 text-white font-medium rounded-lg text-xs hover:bg-[#23306f] transition"
            >
              <Building className="w-3.5 h-3.5 text-[#d4af37]" />
              <span>{isEn ? "Hotels" : "Hotel"}</span>
              <ExternalLink className="w-3 h-3 opacity-75" />
            </a>
          </div>
        </div>

        {/* Programma Giornaliero */}
        {trip.days && trip.days.length > 0 && (
          <div className="space-y-6 mb-12">
            <h2 className="text-2xl font-serif text-[#ede7d8] mb-4">
              {isEn ? "Day-by-Day Program" : "Programma giorno per giorno"}
            </h2>
            {trip.days.map((day, idx) => {
              const dayNum = day.day || idx + 1;
              const currentStyle = dayStyles[dayNum] || 'mix';
              const isThisDayLoading = regeneratingDay === dayNum;

              return (
                <div key={idx} className="bg-[#12204f]/40 border border-gray-800/80 rounded-xl p-5 md:p-6 relative overflow-hidden">
                  
                  {/* OVERLAY DI CARICAMENTO PER LA SINGOLA GIORNATA */}
                  {isThisDayLoading && (
                    <div className="absolute inset-0 bg-[#0b1437]/90 z-20 flex flex-col items-center justify-center p-6 text-center backdrop-blur-sm">
                      <RefreshCw className="w-8 h-8 text-[#d4af37] animate-spin mb-3" />
                      <p className="text-sm font-medium text-[#ede7d8]">
                        {isEn ? `The Genie is reworking Day ${dayNum}...` : `Il Genio sta ricalibrando il Giorno ${dayNum}...`}
                      </p>
                      <span className="text-xs text-gray-400 mt-1">
                        {isEn ? "Tailoring new schedule and venues..." : "Adattamento orari e selezione locali in corso..."}
                      </span>
                    </div>
                  )}

                  {/* BARRA SUPERIORE DEL GIORNO CON SELETTORE STILE E PULSANTE RICALIBRA */}
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4 border-b border-gray-800/60 pb-3">
                    <div>
                      <span className="text-sm font-semibold text-[#d4af37] uppercase tracking-wider block">
                        {isEn ? `Day ${dayNum}` : `Giorno ${dayNum}`}
                      </span>
                      {day.date && <span className="text-xs text-gray-400">{day.date}</span>}
                    </div>

                    {/* Controlli per cambiare stile e rigenerare la giornata */}
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="flex items-center bg-[#0b1437] p-1 rounded-lg border border-gray-700/70">
                        {styleOptions.map(opt => {
                          const IconComp = opt.icon;
                          const isSelected = currentStyle === opt.id;
                          return (
                            <button
                              key={opt.id}
                              type="button"
                              onClick={() => handleDayStyleChange(dayNum, opt.id)}
                              className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium transition ${
                                isSelected
                                  ? 'bg-[#d4af37] text-[#0b1437] font-bold shadow'
                                  : 'text-gray-400 hover:text-white'
                              }`}
                              title={opt.label}
                            >
                              <IconComp className="w-3 h-3" />
                              <span>{opt.label}</span>
                            </button>
                          );
                        })}
                      </div>

                      <button
                        type="button"
                        disabled={isThisDayLoading}
                        onClick={() => handleRegenerateDay(dayNum)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1b2559] hover:bg-[#23306f] border border-[#d4af37]/60 text-[#d4af37] hover:text-[#f1d26b] rounded-lg text-xs font-semibold transition cursor-pointer disabled:opacity-50"
                        title={isEn ? "Rework Day" : "Ricalibra Giorno"}
                      >
                        <RefreshCw className={`w-3.5 h-3.5 ${isThisDayLoading ? 'animate-spin' : ''}`} />
                        <span>{isEn ? "Rework Day" : "Ricalibra"}</span>
                      </button>
                    </div>
                  </div>

                  {day.summary && <p className="text-sm text-gray-300 mb-4 italic">{day.summary}</p>}

                  {day.activities && (
                    <div className="space-y-3">
                      {day.activities.map((act, aIdx) => {
                        const isRestaurant = 
                          act.place_type === 'restaurant' ||
                          /pranzo|cena|ristorante|trattoria|osteria|pizzeria|bistro|dinner|lunch/i.test(act.title || '') ||
                          /pranzo|cena|ristorante|dinner|lunch/i.test(act.time || '');

                        const isTransport = 
                          act.place_type === 'transport' || 
                          /aeroporto|transfer|atterraggio|decollo|volo|airport|flight|landing/i.test(act.title || '');

                        const queryEncoded = encodeURIComponent(`${act.restaurant_name || act.title || ''} ${trip.destination || ''}`);
                        const mapUrl = `https://www.google.com/maps/search/?api=1&query=${queryEncoded}`;

                        return (
                          <div 
                            key={aIdx} 
                            className={`rounded-lg p-4 flex flex-col justify-between gap-3 md:flex-row md:items-center transition ${
                              isRestaurant 
                                ? 'bg-[#152347] border border-amber-500/40 shadow-sm' 
                                : isTransport
                                ? 'bg-[#0f233a] border border-sky-500/30'
                                : 'bg-[#0b1437]/60 border border-gray-800/50'
                            }`}
                          >
                            <div>
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <span className="text-xs font-semibold text-[#d4af37]">{act.time || (isEn ? 'During the day' : 'In giornata')}</span>
                                
                                {isRestaurant && (
                                  <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 font-medium border border-amber-500/30">
                                    <UtensilsCrossed className="w-3 h-3" />
                                    <span>{isEn ? "Recommended Dining" : "Ristorazione consigliata"}</span>
                                  </span>
                                )}

                                {isTransport && (
                                  <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded bg-sky-500/20 text-sky-300 font-medium border border-sky-500/30">
                                    <Plane className="w-3 h-3" />
                                    <span>{isEn ? "Logistics & Transfer" : "Logistica & Transfer"}</span>
                                  </span>
                                )}

                                {act.rating && (
                                  <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-300 font-semibold border border-yellow-500/30">
                                    <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                                    <span>{act.rating}</span>
                                  </span>
                                )}

                                {act.estimated_cost > 0 && (
                                  <span className="text-xs text-gray-400">~{act.estimated_cost} {trip.currency || (isEn ? 'USD' : 'EUR')}</span>
                                )}
                              </div>

                              <h4 className={`text-sm font-semibold ${isRestaurant ? 'text-amber-200' : isTransport ? 'text-sky-200' : 'text-[#ede7d8]'}`}>
                                {act.title}
                              </h4>
                              {act.description && <p className="text-xs text-gray-400 mt-1">{act.description}</p>}
                            </div>

                            <a
                              href={mapUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition whitespace-nowrap self-start md:self-center ${
                                isRestaurant
                                  ? 'bg-amber-400 text-[#0b1437] font-bold hover:bg-amber-300 shadow'
                                  : 'bg-[#d4af37]/10 hover:bg-[#d4af37]/20 border border-[#d4af37]/40 text-[#d4af37]'
                              }`}
                            >
                              <MapPin className="w-3.5 h-3.5" />
                              <span>{isRestaurant ? (isEn ? 'View Venue & Menu' : 'Vedi Locale e Menù') : (isEn ? 'Map' : 'Mappa')}</span>
                              <ExternalLink className="w-3 h-3 opacity-80" />
                            </a>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Supporto / Donazioni */}
        <div className="bg-[#12204f]/40 border border-[#d4af37]/30 rounded-2xl p-6 md:p-8 text-center mt-8 mb-12">
          <span className="text-xs uppercase tracking-widest text-[#d4af37] font-semibold">
            {isEn ? "Enjoying this itinerary?" : "Ti è piaciuto questo itinerario?"}
          </span>
          <h3 className="text-xl md:text-2xl font-serif text-[#ede7d8] mt-1 mb-6">
            {isEn 
              ? "If this helps you travel smarter, treat the Genie to a coffee!" 
              : "Se ti sto aiutando a viaggiare meglio, offri qualcosa al Genio!"}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-lg mx-auto">
            <a 
              href="https://paypal.me/omarferro63/2" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="flex items-center justify-center gap-2.5 px-4 py-3.5 bg-[#795548]/30 hover:bg-[#795548]/60 border border-[#795548]/50 rounded-xl text-sm font-medium text-amber-200 transition shadow"
            >
              <span className="text-xl">☕</span> <span>{isEn ? "Buy me a coffee (2 EUR)" : "Offrimi un caffè (2 EUR)"}</span>
            </a>
            <a 
              href="https://paypal.me/omarferro63/5" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="flex items-center justify-center gap-2.5 px-4 py-3.5 bg-[#d4af37]/20 hover:bg-[#d4af37]/40 border border-[#d4af37]/50 rounded-xl text-sm font-medium text-[#d4af37] transition shadow"
            >
              <span className="text-xl">🥐</span> <span>{isEn ? "Treat me to breakfast (5 EUR)" : "Offrimi la colazione (5 EUR)"}</span>
            </a>
            <a 
              href="https://paypal.me/omarferro63/10" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="flex items-center justify-center gap-2.5 px-4 py-3.5 bg-[#e53935]/20 hover:bg-[#e53935]/40 border border-[#e53935]/50 rounded-xl text-sm font-medium text-red-300 transition shadow"
            >
              <span className="text-xl">🍕</span> <span>{isEn ? "Buy me a pizza (10 EUR)" : "Offrimi una pizza (10 EUR)"}</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
